/**
 * 报销到账（预演 → 可微调 → 确认）—— `DR-048` · 批 83
 *
 * 依据：`Q域PRD` `AC-Q3.4`–`Q3.9` · `AC-Q3.6`（零额外输入 + 可微调 + 预演只读）· `AC-Q6.4`
 *
 * ## 🔴 三条端侧纪律
 *
 * 1. **零自算**：池金额、摘要金额、是否平衡**全部来自服务端**；本组件不出现任何 `/` 或 `*`。
 * 2. **微调要重新预演**：用户改完点「重新计算」⇒ 带 `allocations` 再请求一次预演
 *    （服务端算完再显示）。**不在本地推算**"改了 100 会不会超"。
 * 3. **`issues` 原样展示**：服务端说为什么不能落账，端侧不翻译、不美化（`AC-Q3.6`）。
 *
 * ## 为什么 Body 单独导出
 *
 * `Dialog` 走 Portal，静态渲染（`renderToStaticMarkup`）读不到内容 ⇒
 * 断言必须打在**内容件**上（与 `RestoreDialogBody` / `RollbackConfirmBody` 同范式）。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  AccountView,
  ReimbursePreviewView,
  ReimburseResultView,
} from '@jiaying/shared';
import { confirmReimburse, previewReimburse } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { AccountPicker } from './AccountPicker.js';
import {
  blockingIssues,
  buildConfirmRequest,
  buildPreviewRequest,
  emptyReimburseDraft,
  poolAmountText,
  poolKeyOf,
  poolTitle,
  previewSummaryLines,
  reimburseAmountLabel,
  withPoolOverride,
  type ReimburseDraft,
} from '../features/reimburse.js';

function apiMessages(e: unknown): readonly string[] {
  if (!(e instanceof ApiError)) return [String(e)];
  const details = e.body.details as { issues?: unknown } | undefined;
  const issues = Array.isArray(details?.issues)
    ? details.issues.filter((x): x is string => typeof x === 'string')
    : [];
  return issues.length > 0 ? issues : [e.message];
}

export interface ReimburseDialogBodyProps {
  account: AccountView;
  /** 账本本地日历日 */
  today: string;
  /** 可选的收款账户（由页面过滤好 —— 本组件不猜哪些能选） */
  counterOptions: readonly { id: string; label: string }[];
  /** 分类名解析（页面从 `GET /api/categories` 的结果给出；`null` ⇒ 显示 id） */
  categoryNameOf: (categoryId: string) => string | null;
  onDone: (result: ReimburseResultView) => void;
  onClose: () => void;
}

/** 内容件（**受控**、导出 —— 供静态断言穿透 Portal） */
export function ReimburseDialogBody({
  account,
  today,
  counterOptions,
  categoryNameOf,
  onDone,
  onClose,
}: ReimburseDialogBodyProps): ReactElement {
  const [draft, setDraft] = useState<ReimburseDraft>(() => emptyReimburseDraft(today));
  const [preview, setPreview] = useState<ReimbursePreviewView | null>(null);
  const [errors, setErrors] = useState<readonly string[]>([]);
  const [pending, setPending] = useState(false);

  const disabled = pending;

  /** 预演 / 重新预演（带当前微调）—— **唯一的计算入口** */
  const runPreview = (): void => {
    const built = buildPreviewRequest(draft);
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    const confirm = buildConfirmRequest(draft);
    // 微调项非法时不必打服务端（它不是"服务端才知道"的事，是填错了）
    if (draft.counterAccountId.trim() !== '' && !confirm.ok) {
      setErrors(confirm.errors);
      return;
    }
    const allocs = confirm.ok ? confirm.request.allocations : undefined;
    setErrors([]);
    setPending(true);
    previewReimburse(account.id, {
      ...built.request,
      ...(allocs === undefined || allocs.length === 0 ? {} : { allocations: allocs }),
    })
      .then((r) => setPreview(r))
      .catch((e: unknown) => setErrors(apiMessages(e)))
      .finally(() => setPending(false));
  };

  const submit = (): void => {
    const built = buildConfirmRequest(draft);
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    setErrors([]);
    setPending(true);
    confirmReimburse(account.id, built.request)
      .then((r) => onDone(r))
      .catch((e: unknown) => setErrors(apiMessages(e)))
      .finally(() => setPending(false));
  };

  const blocks = preview === null ? [] : blockingIssues(preview);

  return (
    <>
      <DialogTitle>报销到账 · {account.name}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary">
          只填报销额就够了 —— 分摊按该账户下各笔垫付的金额比例自动算；差额留在原支出分类，不会多出一笔收入。
        </Typography>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 1.5 }} data-testid="reimburse-errors">
            <Stack component="ul" sx={{ m: 0, pl: 2 }}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </Stack>
          </Alert>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
          <TextField
            label={reimburseAmountLabel(account.currency)}
            value={draft.amountText}
            onChange={(e) => {
              setDraft((d) => ({ ...d, amountText: e.target.value }));
              setErrors([]);
              setPreview(null);
            }}
            inputProps={{ 'data-testid': 'reimburse-amount', inputMode: 'decimal' }}
            disabled={disabled}
            helperText="公司 / 保险实际打回来的金额"
          />
          <TextField
            label="到账日"
            type="date"
            value={draft.dateText}
            onChange={(e) => {
              setDraft((d) => ({ ...d, dateText: e.target.value }));
              setErrors([]);
              setPreview(null);
            }}
            // 🆕 批 `115`（T4）：原生日期控件（有日历），仍可键入
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'reimburse-date' }}
            disabled={disabled}
          />
          {/*
            🆕 批 `115`（T4）：**换控件不换候选** —— 仍是页面筛好的 `counterOptions`，顺序原样。
            🔴 `testId` 保留 `reimburse-counter`（既有断言靠它）。
          */}
          <AccountPicker
            label="收款账户"
            accounts={counterOptions.map((o) => ({ id: o.id, name: o.label }))}
            value={draft.counterAccountId}
            onChange={(id) => {
              setDraft((d) => ({ ...d, counterAccountId: id }));
              setErrors([]);
            }}
            disabled={disabled}
            testId="reimburse-counter"
            helperText="同币种账户"
          />
        </Box>

        <Button
          variant="outlined"
          onClick={runPreview}
          disabled={disabled}
          sx={{ mt: 1.5 }}
          data-testid="reimburse-preview"
        >
          {preview === null ? '预演分摊' : '重新计算'}
        </Button>

        {preview !== null && (
          <Box sx={{ mt: 2 }} data-testid="reimburse-preview-panel">
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              分摊结果（服务端算的，可改）
            </Typography>

            {previewSummaryLines(preview).map((l) => (
              <Stack key={l.label} direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {l.label}
                </Typography>
                <Typography variant="body2" data-testid={`reimburse-sum-${l.label}`}>
                  {l.value}
                </Typography>
              </Stack>
            ))}

            {blocks.length > 0 && (
              <Alert severity="error" sx={{ mt: 1.5 }} data-testid="reimburse-blocked">
                <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                  {blocks.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </Stack>
              </Alert>
            )}

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
              {preview.pools.map((p) => {
                const key = poolKeyOf(p.categoryId, p.year);
                return (
                  <TextField
                    key={key}
                    label={poolTitle(p, p.categoryId === null ? null : categoryNameOf(p.categoryId))}
                    value={poolAmountText(p, draft)}
                    onChange={(e) => {
                      setDraft((d) => withPoolOverride(d, key, e.target.value));
                      setErrors([]);
                    }}
                    inputProps={{ 'data-testid': `reimburse-pool-${key}`, inputMode: 'decimal' }}
                    disabled={disabled}
                    helperText={
                      p.crossYearIncomeCents > 0
                        ? '往年垫付 ⇒ 不冲减，改后仍会转记收入'
                        : '改完点「重新计算」看合计是否还对得上'
                    }
                  />
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={disabled}>
          取消
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={disabled || preview === null || blocks.length > 0}
          data-testid="reimburse-submit"
        >
          确认落账
        </Button>
      </DialogActions>
    </>
  );
}

export interface ReimburseDialogProps extends ReimburseDialogBodyProps {
  open: boolean;
}

export function ReimburseDialog({ open, onClose, ...rest }: ReimburseDialogProps): ReactElement {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <ReimburseDialogBody {...rest} onClose={onClose} />
    </Dialog>
  );
}
